import { CustomColumnDef, DataTable, Icons } from '@commenda/kakashi';
import { CorporationType, Country } from '@prisma/client';
import { formatShareValue } from './common';
import { TCapTableDataItem } from './types';

const COMPACT_COLUMN_WIDTHS: Record<string, number> = {
  name: 170,
  ownership: 90,
  actualOwnership: 90,
  esopContribution: 90,
  shares: 110,
  shareValue: 120,
};

const CapTable = ({
  data,
  showEsopPool = false,
  isNumberOfSharesApplicable = true,
  sharesSGD = false,
  showAmount = false,
  corporationType,
  incorporationCountry,
  hideCapitalContribution = false,
  shareValueCurrency,
}: {
  data: TCapTableDataItem[];
  showEsopPool?: boolean;
  isNumberOfSharesApplicable?: boolean;
  sharesSGD?: boolean;
  showAmount?: boolean;
  corporationType?: CorporationType;
  incorporationCountry?: Country;
  hideCapitalContribution?: boolean;
  /** ISO currency code; when set, adds a share value (shares × price) column. */
  shareValueCurrency?: string;
}) => {
  const isIndianPvtLtd =
    corporationType === CorporationType.PvtLtd &&
    incorporationCountry === Country.IN;

  const isIndianLLP =
    corporationType === CorporationType.LLP &&
    incorporationCountry === Country.IN;

  const columns: CustomColumnDef<TCapTableDataItem, any>[] = [
    {
      accessorKey: 'name',
      id: 'name',
      accessorFn: (row) => null,
      header: isIndianLLP
        ? 'Name of the partner'
        : isIndianPvtLtd
          ? 'Name of the shareholder'
          : 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3 min-h-[40px]">
          {row.index !== data.length - 1 ? (
            <>
              <div className="bg-gray-100 rounded-lg p-2 w-12 h-12 flex items-center justify-center">
                <Icons.User01 className="text-gray-500" />
              </div>
              <div className="flex flex-col">
                <span className="text-text-primary">{row.original.name}</span>
                <span className="text-sm text-gray-500">
                  {row.original.email}
                </span>
              </div>
            </>
          ) : (
            <div className="w-full text-center text-text-primary text-md">
              Total
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'ownership',
      id: 'ownership',
      accessorFn: (row) => null,
      header: isIndianLLP
        ? 'Ownership percentage'
        : isIndianPvtLtd
          ? '% of ownership'
          : 'Ownership',
      cell: ({ row }) => (
        <span>
          {row.original.ownership ? `${row.original.ownership}%` : '-'}
        </span>
      ),
    },
    ...(showEsopPool
      ? [
          {
            accessorKey: 'actualOwnership',
            id: 'actualOwnership',
            accessorFn: (row) => null,
            header: 'Actual',
            cell: ({ row }) => (
              <span>
                {row.original.actualOwnership
                  ? `${row.original.actualOwnership}%`
                  : '-'}
              </span>
            ),
          },
          {
            accessorKey: 'esopContribution',
            id: 'esopContribution',
            accessorFn: (row) => null,
            header: shareValueCurrency ? 'ESOP %' : 'ESOP Contribution',
            cell: ({ row }) => {
              const formattedValue = row.original.esopContribution;

              return <span>{formattedValue ? `${formattedValue}%` : '-'}</span>;
            },
          },
        ]
      : []),
    ...(isNumberOfSharesApplicable
      ? [
          {
            accessorKey: 'shares',
            id: 'shares',
            accessorFn: (row) => null,
            header: isIndianPvtLtd
              ? 'No. of share'
              : shareValueCurrency
                ? 'Shares'
                : 'Number of Shares',
            cell: ({ row }) => {
              return (
                <span>{row.original.shares ? row.original.shares : '-'}</span>
              );
            },
          },
          ...(shareValueCurrency
            ? [
                {
                  accessorKey: 'shareValue',
                  id: 'shareValue',
                  accessorFn: (row) => null,
                  header: `Value (${shareValueCurrency})`,
                  cell: ({ row }) => (
                    <span>
                      {row.original.shareValue != null
                        ? formatShareValue(
                            row.original.shareValue,
                            shareValueCurrency
                          )
                        : '-'}
                    </span>
                  ),
                },
              ]
            : []),
          ...(sharesSGD
            ? [
                {
                  accessorKey: 'sharesSGD',
                  id: 'sharesSGD',
                  accessorFn: (row) => null,
                  header: 'SGD conversion',
                  cell: ({ row }) => {
                    return <span>{row.original.sharesSGD || '-'}</span>;
                  },
                },
              ]
            : []),
          ...(isIndianPvtLtd
            ? [
                {
                  accessorKey: 'faceValue',
                  id: 'faceValue',
                  accessorFn: (row) => null,
                  header: 'Face value of the shares',
                  cell: ({ row }) => {
                    return (
                      <span>
                        {row.original.faceValue ? row.original.faceValue : '-'}
                      </span>
                    );
                  },
                },
                {
                  accessorKey: 'totalShareCapital',
                  id: 'totalShareCapital',
                  accessorFn: (row) => null,
                  header: 'Total Share capital',
                  cell: ({ row }) => {
                    return (
                      <span>
                        {row.original.totalShareCapital
                          ? row.original.totalShareCapital
                          : '-'}
                      </span>
                    );
                  },
                },
              ]
            : []),
        ]
      : []),
    ...(showAmount && !hideCapitalContribution
      ? [
          {
            accessorKey: 'amount',
            id: 'amount',
            accessorFn: (row) => null,
            header: isIndianPvtLtd
              ? 'Capital Contribution'
              : 'Amount of contribution',
            cell: ({ row }) => {
              return (
                <span>{row.original.amount ? row.original.amount : '-'}</span>
              );
            },
          },
        ]
      : []),
  ];

  const sizedColumns = shareValueCurrency
    ? columns.map((column) => ({
        ...column,
        width: COMPACT_COLUMN_WIDTHS[column.id as string] ?? column.width,
      }))
    : columns;

  return (
    <div className="w-full overflow-clip">
      <DataTable
        columnGroups={[{ columns: sizedColumns, id: 'shares' }]}
        data={data}
        tableClassName="border rounded-2xl overflow-hidden"
        tableContainerClassName="rounded-2xl border"
        emptyStateConfig={{
          title: 'No shareholders',
          description: '',
          action: undefined,
          renderEmptyState: () => null,
        }}
      />
    </div>
  );
};

export default CapTable;
